import { ConnectionStatus, Network } from '@capacitor/network';
import { tlLogger } from '@taleemabad/shared';
import { analyticsService } from '../api/analytics';
import { syncedDBModel } from '../models/synced';
import { userDB } from '../models/user';
import { EventNames } from '../types/analytics';
import { syncManager } from '../utils/sync-manager';
import { downloadManagerService } from './download-manager';

export enum SyncManagerStatus {
  Failed = 'failed',
  Synced = 'synced',
  NeedSync = 'needSync',
  Syncing = 'syncing',
}

const SYNC_PERIOD = 1000 * 60 * 5; // 5 minutes

// Worker is causing some depedancy issues right now
// Will be diving into it later.
// let syncWorker: Worker | null = null;

// if (window.Worker) {
//   syncWorker = useMemo(
//     () => new Worker(new URL('./worker.ts', import.meta.url)),
//     [],
//   );
// }

// useEffect(() => {
//   if (window.Worker && syncWorker) {
//     syncWorker.onmessage = (e: MessageEvent<string>) => {
//       console.log('got result back...', e.data);
//       syncStatus = SyncManagerStatus.synced;
//     };
//   }
// }, [syncWorker]);

export class SyncStateManager {
  syncStatus: SyncManagerStatus;
  syncProgress: number; // 0-100 percentage
  syncMessage: string; // Current sync stage label
  // Per-feature ready flag. Flips to true as soon as the timetable-specific
  // sync endpoint (sync-school-class-timetable) lands its data in Dexie, so
  // the Timetable page can stop spinning without waiting for unrelated tiers
  // (coaching, courses, report cards, announcements, exam generator) to
  // finish. Reset to false at the start of every sync pass.
  timetableSynced: boolean;
  timerId: any;
  isConnected: boolean;
  subscribers: ((status: SyncManagerStatus) => void)[] = [];
  progressSubscribers: ((progress: number, message: string) => void)[] = [];
  timetableSyncedSubscribers: ((synced: boolean) => void)[] = [];

  constructor() {
    this.syncStatus = SyncManagerStatus.Synced;
    this.syncProgress = 0;
    this.syncMessage = '';
    this.timetableSynced = false;
    const syncCheck = async () => {
      // TODO add logic here to
      // see if state is syncing
      // and how long ago it was started.
      // if it was more than SYNC_PERIOD then reset it?

      // getting token from db instead of cache since it takes some time to set on initial load and is not available
      const token = await userDB.getTokenFromDB();
      if (!token) return;
      tlLogger.log('useSyncManager/syncTimer', {
        message: 'Starting timely sync check.',
        timestamp: new Date(),
      });
      this.checkChanges();
    };
    syncCheck();
    this.timerId = setInterval(syncCheck, SYNC_PERIOD);

    this.isConnected = false;
    Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
      tlLogger.log('syncStateManager/networkStatusChange', {
        message: 'Network status changed',
        status,
      });
      this.isConnected = status.connected;
      // sync downloads from native platforms when network is back
      if (this.isConnected) downloadManagerService.syncDownloads();
    });
    this.init();
  }

  async init() {
    this.isConnected = (await Network.getStatus()).connected;
  }

  subscribe(callback: (status: SyncManagerStatus) => void) {
    this.subscribers.push(callback);
  }
  unsubscribe(callback: (status: SyncManagerStatus) => void) {
    const index = this.subscribers.indexOf(callback);
    if (index !== -1) {
      this.subscribers.splice(index, 1);
    }
  }

  subscribeProgress(callback: (progress: number, message: string) => void) {
    this.progressSubscribers.push(callback);
  }

  unsubscribeProgress(callback: (progress: number, message: string) => void) {
    const index = this.progressSubscribers.indexOf(callback);
    if (index !== -1) {
      this.progressSubscribers.splice(index, 1);
    }
  }

  subscribeTimetableSynced(callback: (synced: boolean) => void) {
    this.timetableSyncedSubscribers.push(callback);
  }
  unsubscribeTimetableSynced(callback: (synced: boolean) => void) {
    const index = this.timetableSyncedSubscribers.indexOf(callback);
    if (index !== -1) {
      this.timetableSyncedSubscribers.splice(index, 1);
    }
  }

  // Called by fetchBookChaptersV2 once the timetable endpoint's data has
  // landed in Dexie. Unblocks the Timetable page's initial-sync spinner
  // without coupling it to the global multi-tier sync status.
  markTimetableSynced() {
    if (this.timetableSynced) return;
    this.timetableSynced = true;
    this.timetableSyncedSubscribers.forEach((subscriber) => subscriber(true));
  }

  updateProgress(progress: number, message?: string) {
    this.syncProgress = Math.min(100, Math.max(0, progress));
    if (message !== undefined) {
      this.syncMessage = message;
    }
    this.progressSubscribers.forEach((subscriber) =>
      subscriber(this.syncProgress, this.syncMessage),
    );
  }

  checkChanges = async (_forceSync = false) => {
    const method = 'syncStateManager/checkChanges';
    if (this.syncStatus === SyncManagerStatus.Syncing.toString()) return;
    if (!this.isConnected) {
      return tlLogger.error(method, {
        message: 'Sync cancelled, device is offline.',
      });
    }
    this.syncStatus = SyncManagerStatus.Syncing;
    // Reset per-feature ready flags so subscribers (e.g. the Timetable page)
    // can show their initial-sync state again until this pass refills Dexie.
    this.timetableSynced = false;
    this.timetableSyncedSubscribers.forEach((subscriber) => subscriber(false));
    this.updateSubscribersWithSyncStatus();
    tlLogger.start(method);
    // performSync decides internally whether to push first based on
    // lastSyncedAt; the previous hasUnsyncedChanges() gate was skipping
    // the initial pull on fresh installs.
    await this.performSync();
    tlLogger.end(method);
  };

  updateSubscribersWithSyncStatus() {
    this.subscribers.forEach((subscriber) => subscriber(this.syncStatus));
  }

  performSync = async () => {
    const method = 'syncStateManager/performSync';
    tlLogger.start(method);
    try {
      await syncManager.performSync(null);
      this.syncStatus = SyncManagerStatus.Synced;
      await syncedDBModel.updateLastSyncStatus(SyncManagerStatus.Synced);
    } catch (e) {
      this.syncStatus = SyncManagerStatus.Failed;
      await syncedDBModel.updateLastSyncStatus(SyncManagerStatus.Failed);
      analyticsService.trackEvent(EventNames.syncDataSyncDataFailedError, {
        epError: (e as Error).message,
      });
      tlLogger.error(method, { context: 'Unable to sync data due to', e });
    }
    tlLogger.end(method);
    this.updateSubscribersWithSyncStatus();
  };
}

export const syncStateManager = new SyncStateManager();
